import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';

// Partners with fallback for missing icons
const PARTNERS = [
  // Tier 1: Major tech
  { name: 'Microsoft', url: 'https://microsoft.com', icon: null },
  { name: 'IBM', url: 'https://ibm.com', icon: null },
  { name: 'SAP', url: 'https://sap.com', icon: null },
  { name: 'OpenAI', url: 'https://openai.com', icon: null },
  { name: 'Anthropic', url: 'https://anthropic.com', icon: null },
  { name: 'Palantir', url: 'https://palantir.com', icon: null },
  { name: 'Esri', url: 'https://esri.com', icon: null },
  { name: 'Autodesk', url: 'https://autodesk.com', icon: null },
  { name: 'Zapier', url: 'https://zapier.com', icon: null },
  { name: 'Airtable', url: 'https://airtable.com', icon: null },
  // Tier 2: Innovation ecosystem & specialized tech
  { name: 'NeuralTrust', url: '#', icon: null },
  { name: 'Picvisa', url: 'https://picvisa.com', icon: null },
  { name: 'Tomra', url: 'https://tomra.com', icon: null },
  { name: 'Tecnalia', url: 'https://tecnalia.com', icon: null },
  { name: 'CIRCE', url: 'https://fcirce.es', icon: null },
  { name: 'ITA', url: 'https://ita.es', icon: null },
  { name: 'Patio Campus', url: '#', icon: null },
  { name: 'BackFund', url: '#', icon: null },
  { name: 'Area101', url: '#', icon: null },
  { name: 'Emergent', url: 'https://emergent.sh', icon: null },
  { name: 'OpenSistemas', url: 'https://opensistemas.com', icon: null },
  { name: 'Turing Challenge', url: '#', icon: null },
  { name: 'South Summit', url: 'https://southsummit.co', icon: null },
  { name: 'Bind 4.0', url: 'https://bind40.com', icon: null },
  { name: 'Cursor', url: 'https://cursor.sh', icon: null },
  { name: 'Mailchimp', url: 'https://mailchimp.com', icon: null },
  { name: 'Softr', url: 'https://softr.io', icon: null },
  { name: 'Fireflies', url: 'https://fireflies.ai', icon: null },
  { name: 'HSI', url: '#', icon: null },
  { name: 'MOBA', url: '#', icon: null },
  { name: 'Movisat', url: 'https://movisat.com', icon: null },
  { name: 'Movolytics', url: '#', icon: null },
  { name: 'Distromel', url: '#', icon: null },
  // Tier 3: Own ventures/spinoffs
  { name: 'Lixo', url: '#', icon: null },
  { name: 'HaloTech', url: '#', icon: null },
  { name: 'Biodatabank', url: '#', icon: null },
];

const ROW1 = PARTNERS.slice(0, 18);
const ROW2 = PARTNERS.slice(17);

function PartnerPill({ p }) {
  return (
    <a
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="partners-logo"
      className="flex-shrink-0 flex items-center gap-2.5 px-4 py-2.5 rounded-xl"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        minWidth: '120px',
        opacity: 0.55,
        filter: 'grayscale(0.2)',
        transition: 'opacity 0.2s, filter 0.2s, border-color 0.2s, background 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1';
        e.currentTarget.style.filter = 'grayscale(0)';
        e.currentTarget.style.borderColor = 'rgba(141,194,31,0.25)';
        e.currentTarget.style.background = 'rgba(141,194,31,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0.55';
        e.currentTarget.style.filter = 'grayscale(0.2)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
      }}
    >
      {p.icon ? (
        <img src={p.icon} alt={p.name} style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }} />
      ) : (
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#8DC21F', flexShrink: 0 }} />
      )}
      <span className="text-xs font-semibold text-white/75 whitespace-nowrap">{p.name}</span>
    </a>
  );
}

export default function PartnersSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();

  return (
    <section
      id="partners"
      data-testid="partners-section"
      ref={ref}
      style={{ background: '#000', paddingTop: '6rem', paddingBottom: '6rem' }}
    >
      <div className="v-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag green">{t('partners.tag')}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
            {t('partners.h1a')}{' '}
            <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>
              {t('partners.h1b')}
            </em>
          </h2>
          <p className="text-sm text-white/50 max-w-[42ch] leading-relaxed">{t('partners.sub')}</p>
        </motion.div>
      </div>

      {/* Marquee rows */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 0.2 }}
        data-testid="partners-marquee"
      >
        <div
          className="relative overflow-hidden mb-3"
          style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}
        >
          <div className="flex gap-3 marquee-l">
            {[...ROW1, ...ROW1].map((p, i) => <PartnerPill key={`r1-${i}`} p={p} />)}
          </div>
        </div>
        <div
          className="relative overflow-hidden"
          style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}
        >
          <div className="flex gap-3 marquee-r">
            {[...ROW2, ...ROW2].map((p, i) => <PartnerPill key={`r2-${i}`} p={p} />)}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
